import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Calendar, Users, UserPlus, List, LogOut, HelpCircle, User } from 'lucide-react';
import { supabase } from '../../../../lib/supabase';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  exactPath?: string;
}

export function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [showMenu, setShowMenu] = useState(false);

  const navigationItems: NavItem[] = [
    { 
      label: 'Dashboard', 
      path: '/coordinator', 
      icon: <Home className="w-5 h-5" />,
      exactPath: '/coordinator'
    },
    { 
      label: 'Meetings', 
      path: '/coordinator/meetings', 
      icon: <Calendar className="w-5 h-5" />,
      exactPath: '/coordinator/meetings'
    },
    { 
      label: 'Mentors', 
      path: '/coordinator/mentors', 
      icon: <Users className="w-5 h-5" />,
      exactPath: '/coordinator/mentors'
    },
    { 
      label: 'Mentees', 
      path: '/coordinator/mentees', 
      icon: <Users className="w-5 h-5" />,
      exactPath: '/coordinator/mentees'
    },
    { 
      label: 'New Assignment', 
      path: '/coordinator/assignments/new', 
      icon: <UserPlus className="w-5 h-5" />,
      exactPath: '/coordinator/assignments/new'
    },
    { 
      label: 'Assignments', 
      path: '/coordinator/assignments', 
      icon: <List className="w-5 h-5" />,
      exactPath: '/coordinator/assignments'
    }
  ];

  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.email) {
        setUserEmail(user.email);
      }
    };
    getUser();
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setUserEmail(null);
    setShowMenu(false);
    navigate('/auth/login');
  };

  const isActivePath = (item: NavItem) => {
    return location.pathname === item.exactPath;
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.user-menu')) {
        setShowMenu(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <aside className="w-64 h-screen bg-white border-r fixed left-0 top-0 flex flex-col">
      {/* Navigation Items */}
      <nav className="flex-1 p-4">
        <div className="space-y-1">
          {navigationItems.map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                isActivePath(item)
                  ? 'bg-[#f0f7f0] text-[#4CAF50]'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              {item.icon}
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* User Profile and Help Section at Bottom */}
      <div className="p-4 border-t space-y-2">
        {userEmail && (
          <div className="relative user-menu">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu(!showMenu);
              }}
              className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="w-10 h-10 rounded-full bg-[#4CAF50] flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 text-left">
                <h2 className="font-semibold text-gray-900">
                  {userEmail.split('@')[0]}
                </h2>
                <p className="text-sm text-gray-500 truncate">{userEmail}</p>
              </div>
            </button>

            {/* Flyout Menu */}
            {showMenu && (
              <div className="absolute bottom-full left-0 mb-2 w-full bg-white rounded-lg shadow-lg border py-1">
                <button
                  onClick={handleSignOut}
                  className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                >
                  <LogOut className="w-4 h-4" />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        )}
        <button
          onClick={() => navigate('/help')}
          className="w-full flex items-center gap-3 px-3 py-2 text-gray-700 hover:bg-gray-50 rounded-lg"
        >
          <HelpCircle className="w-5 h-5" />
          <span>Help</span>
        </button>
      </div>
    </aside>
  );
} 